import { MappedDominoV2 as MappedDomino } from '@toggle/design-system';
import { Domino, Entity, Episode, formatTicker, Period } from '@toggle/toggle';

// Requirements: https://app.clickup.com/t/8693w5wr3
export const getLargestEpisodeByPeriod = (
  dominos: Domino[],
  period: Period
): Episode | undefined => {
  let largestEpisode: Episode | undefined;
  let largestPastEpisodesCount = 0;

  dominos.forEach(domino => {
    const currentEpisode = getEpisodeByPeriod(domino.episodes, period);

    if (!currentEpisode) {
      return;
    }

    const currentPastEpisodesCount = currentEpisode.dates.length;

    if (currentPastEpisodesCount > largestPastEpisodesCount) {
      largestPastEpisodesCount = currentPastEpisodesCount;
      largestEpisode = currentEpisode;
      return;
    }

    if (currentPastEpisodesCount === largestPastEpisodesCount) {
      const currentTradingDays = getTradingDaysCount(currentEpisode);
      const largestTradingDays = largestEpisode
        ? getTradingDaysCount(largestEpisode)
        : 0;

      largestEpisode =
        currentTradingDays > largestTradingDays
          ? currentEpisode
          : largestEpisode;
    }
  });

  return largestEpisode;
};

export const getLabel = (tag: string, entities: Entity[]): string => {
  const entity = entities.find(e => e.tag === tag);

  if (!entity) {
    return tag;
  }

  return formatTicker(entity);
};

export const getTooltipLabel = (tag: string, entities: Entity[]): string => {
  const entity = entities.find(e => e.tag === tag);

  if (!entity) {
    return tag;
  }

  return entity.name_full;
};

export type MappedDominosByPeriod = Record<Period, MappedDomino[]>;

export const mapDominosByPeriod = (
  dominos: Domino[],
  entities: Entity[]
): MappedDominosByPeriod => {
  return dominos.reduce((accumulator, domino) => {
    if (domino) {
      domino.episodes.forEach(episode => {
        const period = accumulator[episode.period] || [];
        const median = episode.median;
        const label = getLabel(domino.entity, entities);
        const tooltipLabel = getTooltipLabel(domino.entity, entities);
        period.push({ tag: domino.entity, label, median, tooltipLabel });
        accumulator[episode.period] = period;
      });
    }

    return accumulator;
  }, {} as MappedDominosByPeriod);
};

export const RELATIVE_IDX_MIN = -21;
export const RELATIVE_IDX_MAX = 126;

export const filterPredictions = (dominos?: Domino[]) =>
  dominos?.map(domino => ({
    ...domino,
    prediction: domino.prediction.filter(
      ({ relative_idx }) =>
        relative_idx >= RELATIVE_IDX_MIN && relative_idx <= RELATIVE_IDX_MAX
    ),
  }));

export const getEpisodeByPeriod = (episodes: Episode[], period: Period) =>
  episodes.find(episode => episode.period === period);

export const getPastEpisodesCount = (episode?: Episode): number =>
  episode?.dates.length ?? 0;

export const getTradingDaysCount = (episode?: Episode): number =>
  episode?.dates.reduce((total, date) => total + (date.data?.length ?? 0), 0) ??
  0;
